/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

import { call, put, takeLatest } from 'redux-saga/effects';
import request from 'modules/common/utils/request';
import { alertActions } from './slice';
import { API } from './constants';

/**
 * Get alert table data generator function to get alert data for a user
 * @param {*} param0
 */
export function* getAlertTableDataGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_ALERT_TABLE_DATA, payload);
    yield put(alertActions.getAlertTableDataSucceeded(response));
  } catch (error) {
    yield put(alertActions.getAlertTableDataFailed(error?.message));
  }
}

/**
 * Get alert table data generator function to get alert data for a user
 * @param {*} param0
 */
export function* getAuditLogDataGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_ALERT_LOG_TABLE_DATA, payload);
    yield put(alertActions.getAuditLogTableDataSucceeded(response));
  } catch (error) {
    yield put(alertActions.getAuditLogTableDataFailed(error?.message));
  }
}

/**
 * Get alert remarks data generator function
 * @param {*} param0
 */
export function* getAlertRemarkDataGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_ALERT_LOG_TABLE_DATA, payload);
    yield put(alertActions.getAlertRemarksSucceeded(response));
  } catch (error) {
    yield put(alertActions.getAlertRemarksFailed(error?.message));
  }
}

/**
 * Get alert table data generator function to get alert data for a user
 * @param {*} param0
 */
export function* getAlertExcelGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_ALERT_ATTACHMENT_EXCEL, payload);
    yield put(alertActions.getAlertExcelSucceeded(response));
  } catch (error) {
    yield put(alertActions.getAlertExcelFailed(error?.message));
  }
}

/**
 * Get semantic remarks for PDF export generator function; hits the same
 * endpoint as getAlertRemarks but stores to a separate slice field so the
 * drawer's alertRemarks state is not touched.
 * @param {*} param0
 */
export function* getAlertPdfGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_ALERT_LOG_TABLE_DATA, payload);
    yield put(alertActions.getAlertPdfSucceeded(response));
  } catch (error) {
    yield put(alertActions.getAlertPdfFailed(error?.message));
  }
}

/**
 * Create alert parameter generator function
 * @param {*} param0
 */
export function* createAlertGenerator({ payload }) {
  try {
    const response = yield call(request, API.POST_ALERT, payload);
    yield put(alertActions.createAlertSucceeded(response));
  } catch (error) {
    yield put(alertActions.createAlertFailed(error?.message));
  }
}

/**
 * Update alert generator function to update alert
 * @param {*} param0
 */
export function* updateAlertGenerator({ payload }) {
  try {
    const response = yield call(request, API.PATCH_ALERT, payload);
    yield put(alertActions.updateAlertSucceeded(response));
  } catch (error) {
    yield put(alertActions.updateAlertFailed(error?.message));
  }
}

/**
 * Delete alert generator function to delete alert
 * @param {*} param0
 */
export function* deleteAlertGenerator({ payload }) {
  try {
    const response = yield call(request, API.DELETE_ALERT, payload);
    yield put(
      alertActions.deleteAlertSucceeded({
        response,
        data: payload,
      })
    );
  } catch (error) {
    yield put(alertActions.deleteAlertFailed(error?.message));
  }
}

/**
 * Get alert parameter generator function
 * @param {*} param0
 */
export function* getAlertParamGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_ALERT_PARAM, payload);
    yield put(alertActions.getAlertParamSucceeded(response));
  } catch (error) {
    yield put(alertActions.getAlertParamFailed(error?.message));
  }
}

/**
 * Update subscription data generator
 * @param {*} param0
 */
export function* updateSubscriptionGenerator({ payload }) {
  try {
    const response = yield call(request, API.PATCH_USER_SUBSCRIPTIONS, payload);
    yield put(alertActions.updateUserSubscriptionsSucceeded(response));
  } catch (error) {
    yield put(alertActions.updateUserSubscriptionsFailed(error?.message));
  }
}

/**
 * Get subscription data generator
 * @param {*} param0
 */
export function* getSubscriptionGenerator({ payload }) {
  try {
    const response = yield call(request, API.GET_USER_SUBSCRIPTIONS, payload);
    yield put(alertActions.getSubscriptionsSucceeded(response));
  } catch (error) {
    yield put(alertActions.getSubscriptionsFailed(error?.message));
  }
}

//
export function* alertSaga() {
  yield takeLatest(alertActions.getAlertTableData.type, getAlertTableDataGenerator);
  yield takeLatest(alertActions.createAlert.type, createAlertGenerator);
  yield takeLatest(alertActions.updateAlert.type, updateAlertGenerator);
  yield takeLatest(alertActions.deleteAlert.type, deleteAlertGenerator);
  yield takeLatest(alertActions.getAlertParam.type, getAlertParamGenerator);
  yield takeLatest(alertActions.getAuditLogTableData.type, getAuditLogDataGenerator);
  yield takeLatest(alertActions.updateUserSubscriptions.type, updateSubscriptionGenerator);
  yield takeLatest(alertActions.getSubscriptions.type, getSubscriptionGenerator);
  yield takeLatest(alertActions.getAlertExcel.type, getAlertExcelGenerator);
  yield takeLatest(alertActions.getAlertPdf.type, getAlertPdfGenerator);
  yield takeLatest(alertActions.getAlertRemarks.type, getAlertRemarkDataGenerator);
}
//
export default alertSaga;
